// Outreach Message Generation Types for CARR Biosystems
// v33: SegmentData fields aligned with classifier v4.2 output
//   - Classifier writes to segment_data.classification (nested) AND segment_data.* (root)
//   - Frontend flattens .classification to root in useCampaignOutreach.ts for backwards compat
//   - EF reads SegmentData fields at root level (persona_summary, messaging_angle, etc.)
//   - messaging_angle values: strategic_partnership | technical_expertise | scientific_collaboration
//     | quality_assurance | facility_engineering | supply_chain_reliability
// Single messaging variant: "traditional" (Path to Patients removed May 2026)

export type OutreachMessageType = 'linkedin_connection_request' | 'linkedin_inmail' | 'linkedin_connection_followup' | 'linkedin_second_touch' | 'email_cold' | 'email_followup';
export type MessagingVariant = 'traditional';

export interface OutreachSender {
  id: string;
  /**
   * DISPLAY name only (may be an alias, e.g. 'Jake Andrews').
   * Never persist this to the database — use `canonicalName`.
   */
  name: string;
  /**
   * Canonical `team_profiles.full_name` (e.g. 'Jacob Andrews').
   * This is the ONLY value that may be written to `outreach_messages.sender_name`
   * or compared against `sending_personas.full_name`.
   */
  canonicalName: string;
  headline: string | null;
  about: string | null;
  positionDescription: string | null;
  /** HeyReach LinkedIn account ID (string of digits). Used by the connection guardrail. */
  heyreachAccountId: string | null;
  /** Per-rep writing rules from team_profiles (Track 3, May 2026) */
  writingRules?: {
    writing_guidelines?: string;
    required_phrases?: string[];
    banned_phrases?: string[];
  } | null;
}

export interface OutreachContactData {
  id: string;
  name: string;
  firstName?: string;
  title?: string;
  headline?: string;
  summary?: string;
  seniority?: string;
  department?: string;
  roleType?: string;
  buyerSegment?: string;
  keywords?: {
    manufacturing?: boolean;
    gmp?: boolean;
    clinical?: boolean;
    cdmo?: boolean;
    scaleUp?: boolean;
    processDev?: boolean;
  };
  location?: string;
  experienceTitle?: string;
  experienceCompany?: string;
  linkedinUrl?: string;
  email?: string;
  accountDomain?: string;
  // Clay enrichment fields (v28)
  clayHeadline?: string;
  claySummary?: string;
  biography?: string;
  marketingPersona?: string;
  // Canonical CARR classification (carr_seniority / carr_department). Renamed from
  // seniorityNormalized / departmentNormalized on 2026-07-30 when the legacy
  // seniority_normalized / department_normalized columns were retired.
  carrSeniority?: string;
  carrDepartment?: string;
}

// Level 2 classification output from buyer_segment_classifier
export interface SegmentData {
  buyer_segment?: string;
  seniority?: string;
  confidence?: string;
  persona_summary?: string;
  primary_pain_points?: string[];
  messaging_angle?: string;
  value_hook?: string;
  decision_role?: string;
  buying_context?: string;
  sso_relevance?: string;
  reasoning?: string;
}

// Enrichment context passed to EF
export interface EnrichmentContext {
  segmentData?: SegmentData;
  clayHeadline?: string;
  claySummary?: string;
  clayExperience?: string;
  clayPublications?: string;
  clayEducation?: string;
  engagementHistory?: {
    connectionStatus?: string;
    previousMessages?: number;
    lastInteraction?: string;
    campaignName?: string;
  };
  classificationLevel?: 'level_1' | 'level_2' | 'none';
  buyerSegment?: string;
}

export interface OutreachAccountData {
  id?: string;
  name: string;
  domain?: string;
  description?: string;
  category?: string;
  modality?: string;
  developmentStage?: string;
  size?: string;
  country?: string;
  state?: string;
  // CARR enrichment fields
  centrifugationRelevance?: boolean;
  manufacturingScale?: string;
  separationTechnology?: string;
  bioreactorType?: string;
  keyPartnerships?: string;
  // Account-level ICP classification (v31)
  icpSegment?: string;
  clinicalPhase?: string;
  therapyCategory?: string;
  platformType?: string;
}

export interface OutreachGenerationPayload {
  messageType: OutreachMessageType;
  messagingVariant?: MessagingVariant;
  sender: {
    name: string;
    headline?: string;
    about?: string;
    positionDescription?: string;
  };
  contact: {
    name: string;
    firstName?: string;
    title?: string;
    headline?: string;
    summary?: string;
    seniority?: string;
    department?: string;
    roleType?: string;
    keywords?: OutreachContactData['keywords'];
    location?: string;
    experienceTitle?: string;
    experienceCompany?: string;
    // Clay enrichment fields (v28)
    clayHeadline?: string;
    claySummary?: string;
    biography?: string;
    marketingPersona?: string;
    buyerSegment?: string;
    // Canonical CARR classification (carr_seniority / carr_department).
    carrSeniority?: string;
    carrDepartment?: string;
  };
  account: {
    name: string;
    domain?: string;
    description?: string;
    category?: string;
    modality?: string;
    developmentStage?: string;
    size?: string;
    country?: string;
    state?: string;
    centrifugationRelevance?: boolean;
    manufacturingScale?: string;
    separationTechnology?: string;
    bioreactorType?: string;
    keyPartnerships?: string;
    enrichmentData?: {
      centrifugationRelevance?: boolean;
      manufacturingScale?: string;
      separationTechnology?: string;
      bioreactorType?: string;
      keyPartnerships?: string;
    };
    icpSegment?: string;
    clinicalPhase?: string;
    therapyCategory?: string;
    platformType?: string;
  };
  segmentData?: SegmentData;
  enrichmentContext?: EnrichmentContext;
  customContext?: string;
  senderProfileId?: string;
  // linkedin_second_touch — the earlier note the contact never replied to, so
  // the regenerate picks a DIFFERENT angle instead of repeating it.
  priorMessageText?: string;
}

export interface OutreachGenerationResponse {
  success: boolean;
  message: string;
  metadata: {
    model: string;
    messageType: OutreachMessageType;
    messagingVariant: MessagingVariant;
    messagingAngle: string;
    messagingSource?: string | null;
    matrixMatchScore?: number | null;
    hookType?: string | null;
    classificationLevel?: 'level_1' | 'level_2' | 'none';
    personaUsed?: boolean;
    icpSegment?: string | null;
    accountCategory: string;
    accountModality?: string | null;
    accountPlatformType?: string | null;
    marketingPersona?: string | null;
    maxChars: number;
    actualChars: number;
    painPointsUsed?: string[];
    valuePropsUsed?: string[];
    enrichmentSources?: string[];
    clayDataUsed?: boolean;
    accountEnrichmentUsed?: boolean;
  };
}

export type OutreachMessageStatus =
  | 'draft'
  | 'ready_to_send'
  | 'loaded'
  | 'sent'
  | 'failed'
  | 'bounced'
  | 'replied'
  | 'waiting_for_connection'
  | 'waiting_for_conversation'
  | 'archived'
  | 'skipped';

export type SkipReasonCategory =
  | 'manually_sent_followup'
  | 'wrong_persona'
  | 'not_a_fit'
  | 'bad_timing'
  | 'already_customer'
  | 'already_engaged'
  | 'do_not_contact'
  | 'duplicate'
  | 'other';

export const SKIP_REASON_CATEGORIES: { value: SkipReasonCategory; label: string; description: string }[] = [
  { value: 'manually_sent_followup', label: 'Manually sent follow-up', description: 'I already followed up outside the tool (LinkedIn, email, etc.). Clears the task and counts as followed up — not a skip.' },
  { value: 'wrong_persona',    label: 'Wrong persona',        description: 'Role/title does not match our buyer personas.' },
  { value: 'not_a_fit',        label: 'Not a fit',             description: 'Company or modality not aligned with CARR.' },
  { value: 'bad_timing',       label: 'Bad timing',            description: 'Contact is unreachable right now; revisit later.' },
  { value: 'already_customer', label: 'Already a customer',    description: 'Account is already in the CARR customer base.' },
  { value: 'already_engaged',  label: 'Already engaged',       description: 'Active conversation in another channel/campaign.' },
  { value: 'do_not_contact',   label: 'Do not contact',        description: 'Opt-out, legal, or relationship reason.' },
  { value: 'duplicate',        label: 'Duplicate',             description: 'Same person already reached in another contact record.' },
  { value: 'other',            label: 'Other (explain)',       description: 'Use the notes field below.' },
];

export type DeliveryChannel = 'linkedin_automation' | 'email_automation' | 'manual';

export interface SavedOutreachMessage {
  id: string;
  contact_id: string;
  contact_name: string;
  contact_title: string | null;
  account_id: string | null;
  account_name: string | null;
  sender_id: string;
  sender_name: string;
  message_type: OutreachMessageType;
  messaging_variant: MessagingVariant;
  generated_text: string;
  final_text: string | null;
  role_type_used: string | null;
  messaging_angle: string | null;
  account_category: string | null;
  custom_context: string | null;
  generation_metadata: Record<string, unknown>;
  enrichment_context: Record<string, unknown>;
  status: OutreachMessageStatus;
  campaign_id: string | null;
  contact_email: string | null;
  contact_linkedin_url: string | null;
  delivery_channel: DeliveryChannel | null;
  sent_at: string | null;
  skip_reason_category: SkipReasonCategory | null;
  skip_reason: string | null;
  skipped_at: string | null;
  skipped_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * v33: limitType differentiates LinkedIn-enforced platform caps (hard, never persist over)
 * from taste-only targets (soft warning, rep can override).
 *   - linkedin_connection_request: LinkedIn truncates >300 chars at send time → platform_hard
 *   - linkedin_inmail: LinkedIn rejects bodies >1900-2000 chars → platform_hard
 *   - linkedin_connection_followup: messages over 900 fail delivery → platform_hard
 *   - email_cold/email_followup: no platform-enforced cap → taste
 */
export type MessageLimitType = 'platform_hard' | 'taste';

export const MESSAGE_TYPE_CONFIG: Record<OutreachMessageType, { label: string; maxChars: number; limitType: MessageLimitType; icon: string }> = {
  linkedin_connection_request: { label: 'Connection Request', maxChars: 300, limitType: 'platform_hard', icon: 'UserPlus' },
  linkedin_inmail: { label: 'LinkedIn InMail', maxChars: 1900, limitType: 'platform_hard', icon: 'Mail' },
  linkedin_connection_followup: { label: 'Connection Follow-up', maxChars: 900, limitType: 'platform_hard', icon: 'MessageCircle' },
  linkedin_second_touch: { label: 'Second Touch', maxChars: 900, limitType: 'platform_hard', icon: 'MessageCircle' },
  email_cold: { label: 'Cold Email', maxChars: 600, limitType: 'taste', icon: 'Send' },
  email_followup: { label: 'Follow-up Email', maxChars: 400, limitType: 'taste', icon: 'Reply' },
};

export const MESSAGING_VARIANT_CONFIG: Record<MessagingVariant, { label: string; description: string }> = {
  traditional: { label: 'Traditional', description: 'Standard industry connection approach' },
};

export const OUTREACH_SENDERS_FILTER = [
  'Jacob Andrews',
  'Bethanne Deuel',
  'Andrew Hallet',
  'Nico Summaria',
  'Jeff Plambeck',
];

/**
 * Canonical `team_profiles.full_name` → friendly display name.
 * PRESENTATION ONLY. A display name must never be persisted or used as a join key:
 * doing so split Jacob's send counts across 'Jacob Andrews' (533) and 'Jake Andrews' (65)
 * and made `isMyCampaign` permanently false for him. Use the helpers below.
 */
export const SENDER_DISPLAY_NAMES: Record<string, string> = {
  'Jacob Andrews': 'Jake Andrews',
};

// Both maps are keyed on the LOWERCASED name. Case-sensitive lookups silently failed
// to resolve values whose casing differed from the constant, which would have
// reintroduced the very split these helpers exist to prevent.
const SENDER_DISPLAY_BY_LOWER: Record<string, string> = Object.fromEntries(
  Object.entries(SENDER_DISPLAY_NAMES).map(([canonical, display]) => [canonical.toLowerCase(), display]),
);

/** Reverse map: display name → canonical name. Built from SENDER_DISPLAY_NAMES. */
const SENDER_CANONICAL_BY_LOWER: Record<string, string> = Object.fromEntries(
  Object.entries(SENDER_DISPLAY_NAMES).map(([canonical, display]) => [display.toLowerCase(), canonical]),
);

/** Canonical name → what the user should see. Unknown names pass through unchanged. */
export function toSenderDisplayName(canonicalName: string): string {
  return SENDER_DISPLAY_BY_LOWER[canonicalName?.toLowerCase()] || canonicalName;
}

/** Any known name (canonical or display) → canonical name. Safe to call on either. */
export function toSenderCanonicalName(anyName: string): string {
  return SENDER_CANONICAL_BY_LOWER[anyName?.toLowerCase()] || anyName;
}

/**
 * True when two names refer to the same person, regardless of which side
 * is an alias. Case-insensitive. Use this instead of `a === b` anywhere
 * a persona name is compared to a sender name.
 */
export function isSameSenderName(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return toSenderCanonicalName(a).toLowerCase() === toSenderCanonicalName(b).toLowerCase();
}

// ── Contact shape passed into outreach modals ────────────────────────
export interface CampaignOutreachContact {
  contact_id: string;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  job_title?: string | null;
  company_name?: string | null;
  company_id?: string | null;
  account_domain?: string | null;
  email?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  headline?: string | null;
  seniority_level?: string | null;
  department?: string | null;
}
